import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { TESTIDS, SCHEDULE_FORM_URL } from "@/lib/site";
import { trackWhatsAppClick } from "@/lib/analytics";
import HeartWaveMark from "@/components/ui/HeartWaveMark";

const links = [
  { id: "inicio", label: "Inicio", testid: TESTIDS.nav.linkHome },
  { id: "sobre-mi", label: "Sobre mí", testid: TESTIDS.nav.linkAbout },
  { id: "servicios", label: "Servicios", testid: TESTIDS.nav.linkServices },
  { id: "tratamientos", label: "Tratamientos", testid: TESTIDS.nav.linkTreatments },
  { id: "preguntas", label: "Preguntas", testid: TESTIDS.nav.linkFaq },
  { id: "contacto", label: "Contacto", testid: TESTIDS.nav.linkContact },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid={TESTIDS.nav.container}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-warm-white/85 backdrop-blur-md border-b border-dusty-rose/20 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between">
        <button
          data-testid={TESTIDS.nav.logo}
          onClick={() => scrollTo("inicio")}
          className="flex items-center gap-3 group"
          aria-label="Berenice Pavón — Inicio"
        >
          <span className="relative inline-flex w-11 h-11 items-center justify-center rounded-full bg-warm-white border border-dusty-rose/40 group-hover:border-accent-magenta/50 transition-colors text-accent-magenta shadow-sm">
            <HeartWaveMark className="w-6 h-6" strokeWidth={1.4} />
          </span>
          <span className="font-serif text-[1.35rem] tracking-tight text-editorial-dark leading-none">
            Berenice <span className="italic text-accent-magenta">Pavón</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => scrollTo(l.id)}
              className="text-[0.93rem] text-editorial-gray hover:text-accent-magenta transition-colors editorial-link"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:block">
          <a
            data-testid={TESTIDS.nav.ctaSchedule}
            href={SCHEDULE_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackWhatsAppClick("navbar_cta")}
            className="inline-flex items-center gap-2 rounded-full bg-accent-magenta px-5 py-2.5 text-sm text-warm-white hover:bg-accent-magenta/90 transition-all hover:-translate-y-0.5 shadow-sm shadow-accent-magenta/20"
          >
            Agendar consulta
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </a>
        </div>

        <button
          data-testid={TESTIDS.nav.mobileToggle}
          className="md:hidden p-2 text-editorial-dark"
          onClick={() => setOpen((s) => !s)}
          aria-label="Menú"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-warm-white/95 backdrop-blur-md border-t border-dusty-rose/20 animate-fade-in">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`${l.testid}-mobile`}
                onClick={() => scrollTo(l.id)}
                className="text-left text-editorial-dark font-serif text-xl"
              >
                {l.label}
              </button>
            ))}
            <a
              href={SCHEDULE_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick("navbar_mobile_cta")}
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-accent-magenta px-5 py-3 text-warm-white"
            >
              Agendar consulta
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
